import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Grid,
  useTheme, // Import useTheme
} from "@mui/material";
import { motion } from "framer-motion"; // Import motion

const Contact = () => {
  const theme = useTheme(); // Use the theme
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submissionError, setSubmissionError] = useState(false);
  const [isDisabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Your handleChange, validate, and handleSubmit functions remain the same
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validate = () => {
    let errors = {};
    if (!formData.name) errors.name = "Name is required";
    if (!formData.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = "Email is invalid";
    if (!formData.message) errors.message = "Message is required";
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDisabled) {
      alert("Form submissions are currently disabled.");
      setFormSubmitted(true);
      return;
    }
    const errors = validate();
    if (Object.keys(errors).length === 0) {
      try {
        await axios.post("https://formspree.io/f/xdkogbrn", formData);
        setFormSubmitted(true);
      } catch (error) {
        setSubmissionError(true);
      }
    } else {
      setFormErrors(errors);
    }
  };

  // Animation variants for the card entrance
  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    // ✅ Wrap the entire component in a motion.div for animation
    <motion.div
      variants={formVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <Box sx={{ mt: 5, mb: 10, px: 2 }}>
        {!formSubmitted ? (
          // ✅ Use a Paper component for a premium, card-like feel
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              maxWidth: "900px",
              mx: "auto",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* ✅ New two-column grid layout */}
            <Grid container spacing={5}>
              {/* Left Column: Text */}
              <Grid item xs={12} md={5}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: "bold",
                    lineHeight: 1.3,
                    mb: 2,
                    // ✅ Gradient title to match the vibe
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.stay} 90%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Get in Touch
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ lineHeight: 1.7, color: "text.secondary" }}
                >
                  Whether you have a question, a feature idea, or just want to
                  say hello—we'd love to hear from you.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 3, color: "text.secondary" }}
                >
                  You can also email us directly at{" "}
                  <Box
                    component="a"
                    href="mailto:info@flybnb.io"
                    sx={{
                      fontWeight: "bold",
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    info@flybnb.io
                  </Box>
                </Typography>
              </Grid>

              {/* Right Column: Form */}
              <Grid item xs={12} md={7}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  autoComplete="off"
                >
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    error={!!formErrors.message}
                    helperText={formErrors.message}
                    sx={{ mb: 3 }}
                  />

                  {/* ✅ New "flashy" button style */}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary" // This will use your theme's main color
                    fullWidth
                    sx={{
                      // Keeping the modern, clean styles
                      fontWeight: "bold",
                      fontSize: "1rem",
                      py: 1.5, // A comfortable padding
                      borderRadius: "12px",
                      textTransform: "none", // Keeps the text as "Send Message"

                      // Removing the flashy gradient and hover effects
                      "&:hover": {
                        // A simple, darker shade on hover
                        backgroundColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Send Message
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Alert
              severity="success"
              sx={{ py: 3, fontSize: "1.25rem", fontWeight: "bold" }}
            >
              🎉 Thank you! We've received your message and will get back to you
              ASAP!
            </Alert>
          </Box>
        )}

        {/* Error Snackbar remains unchanged */}
        {submissionError && (
          <Snackbar
            open={submissionError}
            autoHideDuration={6000}
            onClose={() => setSubmissionError(false)}
          >
            <Alert
              onClose={() => setSubmissionError(false)}
              severity="error"
              sx={{ width: "100%" }}
            >
              Oops! Something went wrong. Please try again later.
            </Alert>
          </Snackbar>
        )}
      </Box>
    </motion.div>
  );
};

export default Contact;
